import { LevelsPath } from './LevelsPath';
import { SummarySection } from './SummarySection';

import type { LevelsPathStep } from './LevelsPath';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { usePortalEvolution } from '@/pages/EmployeePortal/hooks/usePortalReads';
import { getLocalizedText } from '@/utils/localization';

interface FirstEvaluationSectionProps {
    /**
     * Whether the profile itself is still what the employee waits for: none was assigned, or the
     * one that was lists no competency. They then stand on the first stop of the path rather than
     * on the second, and the section names that cause instead of the missing assessment.
     */
    isProfileAwaited?: boolean;
}

/**
 * "Vos niveaux s'afficheront après votre première évaluation": what the summary shows instead of
 * the two competency sections when nothing of the employee has ever been assessed and nothing is
 * waiting for a meeting either.
 *
 * The two sections would each say their own absence, and both would name the wrong cause: neither
 * a competency nor a level is missing, something upstream of them is. So the section says the one
 * cause that holds, and draws what is left before the levels exist.
 */
export const FirstEvaluationSection = ({
    isProfileAwaited = false,
}: FirstEvaluationSectionProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const evolution = usePortalEvolution();
    const managerLabel = getManagerLabel();

    return (
        <SummarySection
            icon={'business-trend-up'}
            title={t('employeePortalSummary.firstEvaluation.title')}
            subtitle={t(
                isProfileAwaited
                    ? 'employeePortalSummary.firstEvaluation.awaitedProfileSubtitle'
                    : 'employeePortalSummary.firstEvaluation.subtitle',
                { managerLabel },
            )}
            evolution={evolution}
            dataTestId={'employee-portal-first-evaluation'}
        >
            {({ currentProfile }) => (
                <FirstEvaluationSteps
                    isProfileAwaited={isProfileAwaited}
                    profileName={currentProfile && getLocalizedText(currentProfile.name)}
                    managerLabel={managerLabel}
                />
            )}
        </SummarySection>
    );
};

interface FirstEvaluationStepsProps {
    /** Whether the employee stands on the profile stop rather than on the assessment. */
    isProfileAwaited: boolean;
    /** The profile the employee was assigned; it names the stop already behind them. */
    profileName: string | null;
    managerLabel: string;
}

/**
 * The three stops between a competency profile and the levels it will hold. The employee stands on
 * the first or on the second of them, and whatever is left is ahead of them either way.
 *
 * None of them carries a date: an assessment that has not happened has neither been submitted nor
 * scheduled anywhere the journey can read, so the path says what comes next and never when.
 */
const FirstEvaluationSteps = ({
    isProfileAwaited,
    profileName,
    managerLabel,
}: FirstEvaluationStepsProps) => {
    const { t } = useAmelioTranslations();

    const steps: LevelsPathStep[] = [
        isProfileAwaited
            ? {
                  id: 'profile',
                  label: t('employeePortalSummary.firstEvaluation.steps.awaitedProfile'),
                  description: t(
                      'employeePortalSummary.firstEvaluation.steps.awaitedProfileOwner',
                      { managerLabel },
                  ),
                  status: 'active',
                  iconName: 'business-personalcard',
              }
            : {
                  id: 'profile',
                  label: t('employeePortalSummary.firstEvaluation.steps.profile'),
                  description: profileName ?? undefined,
                  status: 'completed',
              },
        {
            id: 'evaluation',
            label: t('employeePortalSummary.firstEvaluation.steps.evaluation', { managerLabel }),
            status: isProfileAwaited ? 'upcoming' : 'active',
            iconName: 'edit-clipboard-tick',
        },
        {
            id: 'levels',
            label: t('employeePortalSummary.firstEvaluation.steps.levels'),
            description: t('employeePortalSummary.firstEvaluation.steps.levelsTiming'),
            status: 'upcoming',
            iconName: 'business-status-up',
        },
    ];

    return (
        <LevelsPath
            steps={steps}
            ariaLabel={t('employeePortalSummary.firstEvaluation.steps.ariaLabel')}
            dataTestId={'employee-portal-first-evaluation-steps'}
        />
    );
};
